import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import { useSignUpFlowContext } from 'Views/Onboarding/OnboardingHasUser/OnboardingHasUserContent/OnboardingHasUserContentRouter/SignUpFlow/SignUpFlowContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import { useOnboardingContext } from '../../Contexts/OnboardingContext';

const SubscriptionSetupContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const STRIPE_CREATE_TOKEN_STARTED = 'stripe-create-token-started';
export const STRIPE_CREATE_TOKEN_FAILED = 'stripe-create-token-failed';

export const STRIPE_TOKEN_CREATED_AND_START_PROCESSING = 'process-started';
export const PROCESS_SUCCEEDED = 'process-succeeded';
export const PROCESS_FAILED = 'process-failed';

export const BILLING_INFORMATION_REFRESH_STARTED = 'client-refresh-started';
export const BILLING_INFORMATION_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
export const BILLING_INFORMATION_REFRESH_FAILED = 'client-refresh-failed';

export const TOGGLE_PAYMENT_AUTHORIZED = 'toggle-payment-authorized';

export const RESET_PAYMENT_FORM = 'reset-payment-form';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isProcessing: false,
    paymentProcessed: false,
    processingError: null,
    stripeErrorCode: null,
    stripeErrorType: null,
    stripeErrorMessage: null,
    billingInformationCollection: null,
    isBillingInformationRefreshing: false,
    billingInformationRefreshError: null,
    isPaymentAuthorized: false,
    isCreatingStripeToken: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        billingInformationCollection: action.billingInformationCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case STRIPE_TOKEN_CREATED_AND_START_PROCESSING: {
      return {
        ...state,
        isProcessing: true,
        isCreatingStripeToken: false,
        processingError: null,
      };
    }
    case PROCESS_SUCCEEDED: {
      return {
        ...state,
        isProcessing: false,
        paymentProcessed: true,
        processingError: null,
      };
    }
    case PROCESS_FAILED: {
      return {
        ...state,
        isProcessing: false,
        processingError: action.processingError,
      };
    }
    case BILLING_INFORMATION_REFRESH_STARTED: {
      return {
        ...state,
        isBillingInformationRefreshing: true,
        billingInformationRefreshError: null,
      };
    }
    case BILLING_INFORMATION_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isBillingInformationRefreshing: false,
        billingInformationRefreshError: null,
        billingInformationCollection: action.billingInformationCollection,
      };
    }
    case BILLING_INFORMATION_REFRESH_FAILED: {
      return {
        ...state,
        isBillingInformationRefreshing: false,
        billingInformationRefreshError: action.billingInformationRefreshError,
      };
    }
    case TOGGLE_PAYMENT_AUTHORIZED: {
      return {
        ...state,
        isPaymentAuthorized: action.isPaymentAuthorized,
      };
    }
    case STRIPE_CREATE_TOKEN_STARTED: {
      return {
        ...state,
        isCreatingStripeToken: true,
      };
    }
    case STRIPE_CREATE_TOKEN_FAILED: {
      return {
        ...state,
        isCreatingStripeToken: false,
        stripeErrorCode: action.stripeErrorCode,
        stripeErrorType: action.stripeErrorType,
        stripeErrorMessage: action.stripeErrorMessage,
      };
    }
    case RESET_PAYMENT_FORM: {
      return {
        ...state,
        isProcessing: false,
        isCreatingStripeToken: false,
        processingError: null,
        stripeErrorCode: null,
        stripeErrorType: null,
        stripeErrorMessage: null,
      };
    }
    default: {
      throw new Error(`Unhandled client-agreement-context action type: ${action.type}`);
    }
  }
};

const SubscriptionSetupProvider = ({
  children,
}) => {
  const { householdResource } = useOnboardingContext();
  const { activeStep } = useSignUpFlowContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Subscription Setup',
    isPageView: true,
  });

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleProcessPayment = React.useCallback(async (stripe) => {
    try {
      dispatch({ type: STRIPE_CREATE_TOKEN_STARTED });

      const { token, error } = await stripe.createToken();

      if (error) {
        dispatch({
          type: STRIPE_CREATE_TOKEN_FAILED,
          stripeErrorCode: error.code,
          stripeErrorType: error.type,
          stripeErrorMessage: error.message,
        });
        return;
      }
      dispatch({ type: STRIPE_TOKEN_CREATED_AND_START_PROCESSING });

      const data = {
        attributes: {
          token: token.id,
        },
      };

      await activeStep.relationship(fr.relationships.relatedTask.name)
        .link('related')
        .Post(data, { deprecatedAllowAlterOfRelation: true });

      logStandardizedEvent(categories.digitalOnboarding, eventTypes.completeStep, eventSubTypes.success, { eventProperties: { stepName: 'subscription setup' } });

      dispatch({ type: PROCESS_SUCCEEDED });
    } catch (err) {
      dispatch({ type: PROCESS_FAILED, processingError: err });
    }
  }, [state, activeStep]);

  const refreshBillingInformationCollection = React.useCallback(async () => {
    try {
      dispatch({ type: BILLING_INFORMATION_REFRESH_STARTED });
      const coll = await householdResource.relationship(fr.relationships.billingInformation.name).link('related').get();
      dispatch({ type: BILLING_INFORMATION_REFRESH_SUCCEEDED, billingInformationCollection: coll });
    } catch (err) {
      dispatch({ type: BILLING_INFORMATION_REFRESH_FAILED, billingInformationRefreshError: err });
    }
  }, [householdResource]);

  const resetPaymentForm = () => dispatch({ type: RESET_PAYMENT_FORM });

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      billingInformationCollection: state.billingInformationCollection,
      isBillingInformationRefreshing: state.isBillingInformationRefreshing,
      billingInformationRefreshError: state.billingInformationRefreshError,
      refreshBillingInformationCollection,
      isProcessing: state.isProcessing,
      processingError: state.processingError,
      paymentProcessed: state.paymentProcessed,
      stripeErrorCode: state.stripeErrorCode,
      stripeErrorType: state.stripeErrorType,
      stripeErrorMessage: state.stripeErrorMessage,
      isCreatingStripeToken: state.isCreatingStripeToken,
      hasProcessingError: state.processingError || !!state.stripeErrorCode,
      handleProcessPayment,
      isPaymentAuthorized: state.isPaymentAuthorized,
      handleSetPaymentAuthorizedToggle: (isPaymentAuthorized) => {
        dispatch({
          type: TOGGLE_PAYMENT_AUTHORIZED,
          isPaymentAuthorized,
        });
      },
      resetPaymentForm,
    };
  }, [
    state,
    householdResource,
    handleProcessPayment,
    resetPaymentForm,
    refreshBillingInformationCollection,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });
      const [billingInformationResp] = await Promise.all([
        householdResource.relationship(fr.relationships.billingInformation.name).link('related').get(),
      ]);

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        billingInformationCollection: billingInformationResp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_STARTED, initializationError: err });
    }
  });

  return (
    <SubscriptionSetupContext.Provider value={memoizedValue}>
      {children}
    </SubscriptionSetupContext.Provider>
  );
};

// Use SubscriptionSetup State Context
const useSubscriptionSetupContext = () => {
  const context = React.useContext(SubscriptionSetupContext);
  if (context === undefined) {
    throw new Error('useSubscriptionSetupContext must be used within a SubscriptionSetupProvider');
  }
  return context;
};

SubscriptionSetupProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SubscriptionSetupProvider,
  useSubscriptionSetupContext,
};
